/**
 * Card & Panel Components — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Card: Container for content grouping
 * - Panel: Card with optional header and footer
 * - Variants: default, elevated, bordered
 * - Interactive states (hover, clickable)
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import React, { HTMLAttributes } from 'react';

export type CardVariant = 'default' | 'elevated' | 'bordered';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  interactive?: boolean;
  children: React.ReactNode;
}

const variantStyles: Record<CardVariant, string> = {
  default: `
    bg-[var(--reviewer-card-bg)]
    border border-[var(--reviewer-card-border)]
  `,
  elevated: `
    bg-[var(--reviewer-surface-raised)]
    border border-[var(--reviewer-border-subtle)]
  `,
  bordered: `
    bg-[var(--reviewer-surface)]
    border-2 border-[var(--reviewer-border-strong)]
  `,
};

export function Card({
  variant = 'default',
  interactive = false,
  className = '',
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={`
        p-4
        transition-all duration-150
        ${variantStyles[variant]}
        ${interactive ? 'cursor-pointer hover:border-[var(--reviewer-card-hover-border)]' : ''}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Panel - Card with optional header and footer
 */
export interface PanelProps extends CardProps {
  title?: string;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
}

export function Panel({
  title,
  headerActions,
  footer,
  children,
  className = '',
  ...props
}: PanelProps) {
  return (
    <Card className={`p-0 ${className}`} {...props}>
      {(title || headerActions) && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-[var(--reviewer-border)]">
          {title && (
            <h3 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-lg)] font-medium font-mono m-0">
              {title}
            </h3>
          )}
          {headerActions && <div>{headerActions}</div>}
        </div>
      )}
      
      <div className="p-4">{children}</div>
      
      {footer && (
        <div className="px-4 py-3 border-t border-[var(--reviewer-border)]">
          {footer}
        </div>
      )}
    </Card>
  );
}
